import { useMutation } from '@connectrpc/connect-query'
import { SiteContactMarketingStatus } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/site_contact_marketing_status_pb'
import { FileContent } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/block_schema_pb'
import { createSiteContact } from '@fingertip/creator-proto/gen/fingertip/creator/site_contact/v1/site_contact-SiteContactService_connectquery'
import { ArrowWallDownIcon } from '@fingertip/icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { CheckboxField } from '@/components/fields/checkbox-field'
import { InputField } from '@/components/fields/input-field'
import { Button } from '@/components/ui/button'
import { useToken } from '@/lib/hooks/use-token'

const schema = z.object({
  email: z
    .string()
    .email('Invalid email address')
    .min(1, { message: 'Email address is required' }),
  isSubscribed: z.boolean().optional(),
})

type Props = {
  siteSlug: string
  fileContent: FileContent
  onSuccess: () => void
  buttonText: string
}

export type Schema = z.infer<typeof schema>

export const EmailForm = ({
  siteSlug,
  fileContent,
  onSuccess,
  buttonText,
}: Props) => {
  const { callOptions } = useToken()

  const mutation = useMutation(createSiteContact, {
    callOptions,
    onSuccess: () => {
      toast.success('Success!')
      onSuccess()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const { control, handleSubmit } = useForm<Schema>({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: {
      email: '',
      isSubscribed: true,
    },
  })

  const submitHandler = useCallback(
    async (input: Schema) => {
      mutation.mutate({
        siteSlug,
        source: 'DOWNLOADED_FILE',
        marketingStatus: input.isSubscribed
          ? SiteContactMarketingStatus.SUBSCRIBED
          : undefined,
        context: {
          fileName: fileContent.title,
          fileSrc: fileContent.fileAssetSrc,
        },
        ...input,
      })
    },
    [fileContent.fileAssetSrc, fileContent.title, mutation, siteSlug],
  )

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <InputField
        control={control}
        type="email"
        name="email"
        placeholder="E.g. you@email.com"
      />

      <CheckboxField
        control={control}
        name="isSubscribed"
        label="Join our newsletter"
      />

      <Button className="w-full">
        {buttonText}
        <ArrowWallDownIcon className="size-fluid-4 shrink-0 ml-2" />
      </Button>
    </form>
  )
}
